import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, Shield, Mail, CheckCircle, XCircle, Send, Lock, Database, Users, Heart, Copy, Trash2, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import SediSection from '@/components/SediSection';
import BackupSection from '@/components/BackupSection';
import CasellaRefertiSection from '@/components/CasellaRefertiSection';
import NumeriUtiliSection from '@/components/NumeriUtiliSection';

export default function Impostazioni() {
  const { hasRole } = useAuth();
  const [emailStato, setEmailStato] = useState(null);
  const [cryptoStato, setCryptoStato] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [encrypting, setEncrypting] = useState(false);

  // Famiglia accesses
  const [famigliaAccessi, setFamigliaAccessi] = useState([]);
  const [ospiti, setOspiti] = useState([]);
  const [newAccesso, setNewAccesso] = useState({ ospite_id: '', referente_nome: '', referente_email: '' });
  const [creatingAccesso, setCreatingAccesso] = useState(false);

  const fetchFamigliaAccessi = useCallback(async () => {
    try {
      const res = await api.get('/famiglia/accessi');
      setFamigliaAccessi(res.data);
    } catch (e) { console.error('Errore caricamento accessi famiglia:', e); }
  }, []);

  const fetchOspiti = useCallback(async () => {
    try {
      const res = await api.get('/ospiti');
      setOspiti(res.data);
    } catch (e) { console.error('Errore caricamento ospiti:', e); }
  }, []);

  useEffect(() => {
    if (!hasRole('coordinatore')) return;
    api.get('/email/stato').then(r => setEmailStato(r.data)).catch(() => {});
    api.get('/crittografia/stato').then(r => setCryptoStato(r.data)).catch(() => {});
    fetchFamigliaAccessi();
    fetchOspiti();
  }, [hasRole, fetchFamigliaAccessi, fetchOspiti]);

  const handleTestEmail = async () => {
    if (!testEmail) { toast.error('Inserisci un indirizzo email'); return; }
    setSending(true);
    try {
      await api.post('/email/test', { destinatario: testEmail });
      toast.success('Email di test inviata!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore invio email');
    }
    setSending(false);
  };

  const handleEncryptData = async () => {
    setEncrypting(true);
    try {
      const r = await api.post('/crittografia/cifra-dati-esistenti');
      toast.success(`${r.data.documenti_cifrati} documenti cifrati con successo`);
      api.get('/crittografia/stato').then(r2 => setCryptoStato(r2.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore cifratura');
    }
    setEncrypting(false);
  };

  const handleCreateAccesso = async () => {
    if (!newAccesso.ospite_id || !newAccesso.referente_nome) {
      toast.error('Seleziona un ospite e inserisci il nome del referente');
      return;
    }
    setCreatingAccesso(true);
    try {
      const res = await api.post('/famiglia/accessi', newAccesso);
      toast.success(`Codice accesso: ${res.data.codice}`);
      setNewAccesso({ ospite_id: '', referente_nome: '', referente_email: '' });
      fetchFamigliaAccessi();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione accesso');
    }
    setCreatingAccesso(false);
  };

  const handleRevokeAccesso = async (id) => {
    try {
      await api.delete(`/famiglia/accessi/${id}`);
      toast.success('Accesso revocato');
      fetchFamigliaAccessi();
    } catch {
      toast.error('Errore revoca accesso');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Codice ${code} copiato`);
  };

  if (!hasRole('coordinatore')) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Accesso riservato al Coordinatore</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="impostazioni-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel] tracking-tight flex items-center gap-2">
          <Settings className="w-6 h-6 text-slate-700" />Impostazioni
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Configurazione sistema, sedi, email e sicurezza</p>
      </div>

      <SediSection />

      {/* Email Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-5 h-5 text-brand" />
            Notifiche Email
            {emailStato?.configurato ? (
              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid="email-stato-badge">
                <CheckCircle className="w-3 h-3 mr-1" />Configurato
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid="email-stato-badge">
                <XCircle className="w-3 h-3 mr-1" />Non Configurato
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-2">
            <p className="text-slate-600">
              Il servizio email permette di inviare notifiche automatiche ai familiari degli ospiti
              (esiti visite, riepilogo settimanale).
            </p>
            {!emailStato?.configurato && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-700 space-y-1">
                <p className="font-semibold">Per attivare le email:</p>
                <ol className="list-decimal pl-4 space-y-0.5">
                  <li>Registrati su <a href="https://resend.com/signup" target="_blank" rel="noopener noreferrer" className="underline font-medium">resend.com</a> (gratuito, 100 email/giorno)</li>
                  <li>Copia la tua API Key dalla dashboard</li>
                  <li>Inseriscila nel file <code className="bg-amber-100 px-1 rounded">backend/.env</code> come <code className="bg-amber-100 px-1 rounded">RESEND_API_KEY=re_xxxxx</code></li>
                  <li>Riavvia il backend</li>
                </ol>
              </div>
            )}
            {emailStato?.configurato && (
              <p className="text-xs text-slate-400">Mittente: {emailStato.sender}</p>
            )}
          </div>

          {/* Test email */}
          <div className="border rounded-lg p-4 space-y-3">
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Invia Email di Test</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="esempio@email.com"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                className="flex-1"
                data-testid="test-email-input"
              />
              <Button
                onClick={handleTestEmail}
                disabled={sending || !emailStato?.configurato}
                className="bg-brand hover:bg-brand-dark text-white"
                data-testid="send-test-email-btn"
              >
                <Send className="w-4 h-4 mr-1" />{sending ? 'Invio...' : 'Invia Test'}
              </Button>
            </div>
            {!emailStato?.configurato && (
              <p className="text-[10px] text-slate-400">Configura prima la API key per inviare email</p>
            )}
          </div>

          {/* Recent emails */}
          {emailStato?.email_recenti?.length > 0 && (
            <div className="border rounded-lg p-4 space-y-2">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email Recenti</Label>
              {emailStato.email_recenti.map((e, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                  <div>
                    <span className="font-medium text-slate-700">{e.destinatario}</span>
                    <span className="text-slate-400 ml-2">{e.oggetto}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-[9px] ${e.stato === 'inviata' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {e.stato}
                    </Badge>
                    <span className="text-[10px] text-slate-400">{new Date(e.created_at).toLocaleString('it-IT')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Encryption */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="w-5 h-5 text-emerald-600" />
            Crittografia Dati
            {cryptoStato?.configurata ? (
              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid="crypto-stato-badge">
                <CheckCircle className="w-3 h-3 mr-1" />Attiva
              </Badge>
            ) : (
              <Badge className="bg-red-100 text-red-700 text-[10px]" data-testid="crypto-stato-badge">
                <XCircle className="w-3 h-3 mr-1" />Non Attiva
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-3">
            <p className="text-slate-600">
              La crittografia protegge i dati personali sensibili degli ospiti nel database.
              I dati sono cifrati con algoritmo AES-256 e decifrati automaticamente durante l'uso.
            </p>
            {cryptoStato?.campi_cifrati && (
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-1.5">Campi cifrati per collezione:</p>
                {Object.entries(cryptoStato.campi_cifrati).map(([collection, fields]) => (
                  <div key={collection} className="flex items-center gap-2 mb-1">
                    <Database className="w-3 h-3 text-slate-400" />
                    <span className="text-xs font-medium text-slate-700 capitalize">{collection}:</span>
                    {fields.length > 0 ? (
                      <div className="flex gap-1 flex-wrap">
                        {fields.map(f => (
                          <Badge key={f} variant="outline" className="text-[9px]">{f}</Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-400">Nessun campo cifrato</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {cryptoStato?.configurata && (
            <div className="flex items-center gap-3">
              <Button
                onClick={handleEncryptData}
                disabled={encrypting}
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="encrypt-existing-btn"
              >
                <Shield className="w-4 h-4 mr-1" />{encrypting ? 'Cifratura in corso...' : 'Cifra Dati Esistenti'}
              </Button>
              <p className="text-[10px] text-slate-400">Cifra i dati inseriti prima dell'attivazione della crittografia</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* === PORTALE FAMIGLIE === */}
      <Card className="border-0 shadow-md" data-testid="famiglia-settings-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Heart className="w-4 h-4 text-rose-500" />
            Portale Famiglie
          </CardTitle>
          <p className="text-xs text-slate-500">Gestisci gli accessi dei familiari al portale di consultazione</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Create new access */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-600">Nuovo Accesso Familiare</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Ospite</Label>
                <Select value={newAccesso.ospite_id} onValueChange={v => setNewAccesso(a => ({ ...a, ospite_id: v }))}>
                  <SelectTrigger data-testid="famiglia-select-ospite"><SelectValue placeholder="Seleziona ospite" /></SelectTrigger>
                  <SelectContent>
                    {ospiti.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.cognome} {o.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Nome Referente</Label>
                <Input
                  value={newAccesso.referente_nome}
                  onChange={e => setNewAccesso(a => ({ ...a, referente_nome: e.target.value }))}
                  placeholder="Es. Davide Marchetti"
                  data-testid="famiglia-referente-nome"
                />
              </div>
              <div>
                <Label className="text-xs">Email (opzionale)</Label>
                <Input
                  value={newAccesso.referente_email}
                  onChange={e => setNewAccesso(a => ({ ...a, referente_email: e.target.value }))}
                  placeholder="email@esempio.it"
                  data-testid="famiglia-referente-email"
                />
              </div>
            </div>
            <Button size="sm" onClick={handleCreateAccesso} disabled={creatingAccesso} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="famiglia-create-btn">
              {creatingAccesso ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Crea Accesso
            </Button>
          </div>

          <Separator />

          {/* List existing accesses */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-600">Accessi Attivi</p>
            {famigliaAccessi.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">Nessun accesso famiglia creato</p>
            ) : (
              famigliaAccessi.map(acc => (
                <div key={acc.id} className={`flex items-center gap-3 p-3 rounded-lg border ${acc.attivo ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`} data-testid={`famiglia-accesso-${acc.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{acc.ospite_nome}</p>
                    <p className="text-xs text-slate-400">Referente: {acc.referente_nome} {acc.referente_email && `(${acc.referente_email})`}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-mono text-sm font-bold text-brand bg-blue-50 px-2 py-1 rounded" data-testid={`famiglia-codice-${acc.id}`}>{acc.codice}</span>
                    {acc.attivo && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => copyCode(acc.codice)} className="text-slate-400 hover:text-brand" data-testid={`famiglia-copy-${acc.id}`}>
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleRevokeAccesso(acc.id)} className="text-slate-400 hover:text-red-600" data-testid={`famiglia-revoke-${acc.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    <Badge className={acc.attivo ? 'bg-emerald-100 text-emerald-700 text-[9px]' : 'bg-red-100 text-red-700 text-[9px]'}>
                      {acc.attivo ? 'Attivo' : 'Revocato'}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="text-[10px] text-slate-400">
            I familiari accedono da <span className="font-mono font-semibold">/famiglia</span> inserendo il codice e il cognome dell'ospite.
          </div>
        </CardContent>
      </Card>

      {/* Backup database */}
      <NumeriUtiliSection />

      <CasellaRefertiSection />

      <BackupSection />

      {/* System Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-600" />
            Informazioni Sistema
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Versione</p>
              <p className="text-sm font-bold text-slate-700">2.5.0</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Capacità</p>
              <p className="text-sm font-bold text-slate-700">80 posti</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Email</p>
              <p className="text-sm font-bold text-slate-700">{emailStato?.configurato ? 'Attivo' : 'Non attivo'}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Crittografia</p>
              <p className="text-sm font-bold text-slate-700">{cryptoStato?.configurata ? 'Attiva' : 'Non attiva'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
